import { generatePath } from 'react-router';
import { getTierInfo, isOnFreeTier } from './tiers';
import { initiateTrackStructEvent } from '../components/analytics/Snowplow';
import {
	EVT_ACCOUNT,
	EVT_ELEMENT_INTERACTION,
	EVT_SUBSCRIPTION_CHANGE,
	EVT_CONFIRM,
	EVT_ELEMENT_TYPE_BUTTON,
	EVT_SUBSCRIPTION_CHANGE_CONFIRM,
} from '../constants/SnowplowEvents';
import { ROUTE_ACCOUNT_BEDROCK, ROUTE_ACTIVATE_PLAN } from '../constants/Routes';
import {
	CHANGE_TIER_NOTIFICATION,
	CHANGE_TIER_QUEUED_NOTIFICATION,
} from '../constants/Notifications';
import { TIER_RATEPLAN_SLUGS, TIER_IDS } from '../constants/Tiers';
import { PARAM_NAMES } from '../constants/Checkout';
import { EVT_CHANGE_TIER_SUBMIT, EVT_CHANGE_TIER_ERROR } from '../constants/EventTypes';
import { CONFIRM, DOWNGROADE_CHANGE_TIER_CONFIRM_BUTTON } from '../constants/Copy';
import { TierTitles } from '../constants/TierTitles';
import { showNotification } from '../actions/notificationActions';
import { reportError } from './realUserMonitoring';

const validateRequiredParam = (value, paramName) => {
	if (!value) {
		throw new Error(`${paramName} is required`);
	}
};

const validateGigyaUser = (gigyaUser) => {
	if (!gigyaUser?.UID) {
		throw new Error('Valid Gigya user with UID is required');
	}
};

const validateTierChangeParams = (params) => {
	const { selectedTier, currentTierId, tierCollection, gigyaUser } = params;

	validateRequiredParam(selectedTier, 'selectedTier');
	validateRequiredParam(currentTierId, 'currentTierId');
	validateRequiredParam(tierCollection, 'tierCollection');
	validateGigyaUser(gigyaUser);
};

const trackTierChangeStart = ({ gigyaUser, currentTierId, selectedTier, subscriptionNumber }) => {
	initiateTrackStructEvent({
		category: EVT_ACCOUNT,
		action: EVT_SUBSCRIPTION_CHANGE,
		label: EVT_CONFIRM,
		rtlUser: { rtlUserId: gigyaUser.UID },
		rtlElementContext: {
			elementInteraction: EVT_ELEMENT_INTERACTION,
			elementGroup: EVT_SUBSCRIPTION_CHANGE,
			elementType: EVT_ELEMENT_TYPE_BUTTON,
			elementName: EVT_SUBSCRIPTION_CHANGE_CONFIRM,
			elementLabel: DOWNGROADE_CHANGE_TIER_CONFIRM_BUTTON,
		},
		rtlFunnel: {
			funnelName: EVT_SUBSCRIPTION_CHANGE,
			funnelStepName: 'Confirm Subscription Change',
			funnelStep: '04',
			funnelSubscriptionType: `${currentTierId}|${selectedTier}`,
		},
		...(subscriptionNumber && {
			rtlPurchase: {
				videolandSubscriptionNumber: subscriptionNumber,
			},
		}),
	});
};

const trackTierChangeSubmit = ({ tierInfo, analyticsPush }) => {
	const { pos, price, title } = tierInfo;
	analyticsPush({ productName: title, label: CONFIRM, pos, price }, EVT_CHANGE_TIER_SUBMIT);
};

const trackTierChangeError = ({ error, selectedTier, currentTierId, analyticsPush }) => {
	analyticsPush(
		{
			label: error.message || 'Tier change failed',
			tierId: selectedTier,
			currentTierId,
		},
		EVT_CHANGE_TIER_ERROR
	);
};

const getUpgradeUrl = (selectedTier, showPromo) => {
	const planType = showPromo ? 'promo' : 'evergreen';
	const plan = TIER_RATEPLAN_SLUGS[selectedTier]?.[planType];

	if (!plan) {
		throw new Error(`Invalid plan configuration for tier: ${selectedTier}`);
	}

	return `${generatePath(ROUTE_ACTIVATE_PLAN, { plan })}?${PARAM_NAMES.UPGRADE}=tv-gemist`;
};

const getPostChangeNavigationUrl = (currentTierId, selectedTier) => {
	const tierUpgradePaths = {
		[TIER_IDS.ESSENTIAL]: {
			[TIER_IDS.STANDARD]: `${ROUTE_ACCOUNT_BEDROCK}?switchedTierFrom=${
				TierTitles[TIER_IDS.ESSENTIAL].title
			}`,
			[TIER_IDS.PREMIUM]: `${ROUTE_ACCOUNT_BEDROCK}?switchedTierFrom=${
				TierTitles[TIER_IDS.ESSENTIAL].title
			}`,
		},
		[TIER_IDS.STANDARD]: {
			[TIER_IDS.PREMIUM]: `${ROUTE_ACCOUNT_BEDROCK}?switchedTierFrom=${
				TierTitles[TIER_IDS.STANDARD].title
			}`,
		},
	};

	return tierUpgradePaths[currentTierId]?.[selectedTier] || ROUTE_ACCOUNT_BEDROCK;
};

const handleFreeTierUpgrade = ({ selectedTier, showPromo, history }) => {
	const upgradeUrl = getUpgradeUrl(selectedTier, showPromo);
	history.replace(upgradeUrl);
};

const handleTierChangeSuccess = ({
	switchTier,
	currentTierId,
	selectedTier,
	dispatch,
	history,
}) => {
	if (!switchTier?.effectiveDate) {
		throw new Error('Invalid API response: missing effective date');
	}

	const effectiveDate = new Date(switchTier.effectiveDate);
	const isImmediateChange = effectiveDate <= new Date();

	if (isImmediateChange) {
		dispatch(showNotification(CHANGE_TIER_NOTIFICATION));
		const navigationUrl = getPostChangeNavigationUrl(currentTierId, selectedTier);
		history.push(navigationUrl);
		return;
	}

	dispatch(showNotification(CHANGE_TIER_QUEUED_NOTIFICATION));
	history.push(`${ROUTE_ACCOUNT_BEDROCK}?tierChangeEffectiveDate=${switchTier.effectiveDate}`);
};

const handlePaidTierChange = async ({
	selectedTier,
	currentTierId,
	tierCollection,
	changeSubscriptionTier,
	analyticsPush,
	dispatch,
	history,
}) => {
	const tierInfo = getTierInfo(selectedTier, tierCollection);

	trackTierChangeSubmit({ tierInfo, analyticsPush });

	try {
		const switchTier = await changeSubscriptionTier(selectedTier);

		handleTierChangeSuccess({
			switchTier,
			currentTierId,
			selectedTier,
			dispatch,
			history,
		});
	} catch (error) {
		reportError(error);
		trackTierChangeError({ error, selectedTier, currentTierId, analyticsPush });
		throw error;
	}
};

export const changeTier = async ({
	selectedTier,
	currentTierId,
	tierCollection,
	gigyaUser,
	subscriptionNumber,
	showPromo = false,
	history,
	changeSubscriptionTier,
	analyticsPush,
	dispatch,
}) => {
	try {
		validateTierChangeParams({
			selectedTier,
			currentTierId,
			tierCollection,
			gigyaUser,
		});
	} catch (error) {
		reportError(error);
		throw error;
	}

	trackTierChangeStart({
		gigyaUser,
		currentTierId,
		selectedTier,
		subscriptionNumber,
	});

	if (isOnFreeTier(currentTierId)) {
		handleFreeTierUpgrade({ selectedTier, showPromo, history });
		return;
	}

	await handlePaidTierChange({
		selectedTier,
		currentTierId,
		tierCollection,
		changeSubscriptionTier,
		analyticsPush,
		dispatch,
		history,
	});
};
